import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Instagram, Youtube, Facebook, Twitter, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { PUBLIC_LAUNCH, FACEBOOK_COMMUNITY_URL } from "@/constants/launch";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setBusy(true);
    try {
      await api.post("/newsletter", { email });
      toast.success("Welcome to the Village. Check your inbox.");
      setEmail("");
    } catch {
      toast.error("Something went wrong. Try again.");
    } finally {
      setBusy(false);
    }
  };

  if (PUBLIC_LAUNCH) return (
    <footer className="mt-24 border-t border-border bg-secondary/40 relative">
      <div className="container-x py-16 md:py-24 grid grid-cols-1 md:grid-cols-2 gap-10">
        <div><div className="font-display text-2xl">Wolfdog Village</div><p className="eyebrow mt-1">Learning Institute</p><p className="mt-6 text-sm text-muted-foreground max-w-sm">Educating people. Strengthening relationships. Improving the future of wolfdogs — because every wolfdog deserves the right start.</p></div>
        <nav aria-label="Footer" className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm"><Link to="/learn" className="hover:underline">Learning Center</Link><Link to="/free-apps" className="hover:underline">Your Free Apps</Link><Link to="/community" className="hover:underline">Community & Events</Link><Link to="/resources" className="hover:underline">Resource Library</Link><Link to="/certification" className="hover:underline">Earn a Certificate</Link><Link to="/legal" className="hover:underline">Legal Resources</Link><a href="https://wolfdognews.com/" target="_blank" rel="noopener noreferrer" className="hover:underline">News<span className="sr-only"> (opens in a new tab)</span></a><Link to="/about" className="hover:underline">About Wolfdog Village</Link><Link to="/contact" className="hover:underline">Contact</Link><a href={FACEBOOK_COMMUNITY_URL} target="_blank" rel="noopener noreferrer" className="hover:underline">Facebook Community<span className="sr-only"> (opens in a new tab)</span></a></nav>
      </div>
      <div className="container-x pb-10 font-display text-5xl md:text-7xl tracking-tighter leading-none">Every Wolfdog<br /><span className="italic">deserves the right start.</span></div>
      <div className="border-t border-border"><div className="container-x py-6 text-xs text-muted-foreground">© {new Date().getFullYear()} Wolfdog Village. All rights reserved.</div></div>
    </footer>
  );

  return (
    <footer className="mt-24 border-t border-border bg-secondary/40 relative">
      <div className="container-x py-16 md:py-24 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-10">
        <div className="col-span-2 lg:col-span-2">
          <div className="font-display text-2xl leading-tight">Wolfdog Village</div>
          <p className="eyebrow mt-1">Learning Institute</p>
          <p className="mt-6 text-sm text-muted-foreground max-w-sm">
            Educating people. Strengthening relationships. Improving the future of wolfdogs — because every wolfdog deserves the right start.
          </p>
          <form onSubmit={subscribe} className="mt-6 flex items-stretch gap-0 max-w-sm" data-testid="footer-newsletter">
            <input
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              type="email"
              required
              placeholder="your@email.com"
              data-testid="footer-newsletter-email"
              className="flex-1 min-w-0 px-4 py-3 text-sm rounded-l-md border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary"
            />
            <button
              type="submit"
              disabled={busy}
              data-testid="footer-newsletter-submit"
              className="rounded-r-md bg-primary text-primary-foreground px-4 hover:brightness-110 transition"
            >
              <ArrowRight className="h-4 w-4" />
            </button>
          </form>
          <div className="flex items-center gap-4 mt-6 text-muted-foreground">
            <a href="#" aria-label="Instagram" className="hover:text-foreground"><Instagram className="h-4 w-4" /></a>
            <a href="#" aria-label="YouTube" className="hover:text-foreground"><Youtube className="h-4 w-4" /></a>
            <a
              href="https://www.facebook.com/share/g/1DrvgXMeh3/?mibextid=wwXIfr"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Wolfdog Village Facebook group (opens in a new tab)"
              data-testid="footer-facebook"
              className="hover:text-foreground rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            ><Facebook className="h-4 w-4" /></a>
            <a href="#" aria-label="Twitter" className="hover:text-foreground"><Twitter className="h-4 w-4" /></a>
          </div>
        </div>

        <FooterCol title="Learn" links={[["All Courses","/learn"],["Learning Paths","/learn"],["Certification","/certification"],["Legal Center","/legal"]]} />
        <FooterCol title="Community" links={[["Discussions","/community"],["Member Spotlight","/community"],["Events","/events"],["News","/news"]]} />
        <FooterCol title="Resources" links={[["Library","/resources"],["Directory","/directory"],["Recommended Reading","/resources"],["Contact","/contact"]]} />
        <FooterCol title="About" links={[["Our Mission","/about"],["Our Team","/about"],["Partners","/about"],["Contact","/contact"]]} />
      </div>

      <div className="container-x pb-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div className="font-display text-5xl md:text-7xl lg:text-8xl tracking-tighter leading-none opacity-90">
          Every Wolfdog<br /><span className="italic">deserves the right start.</span>
        </div>
      </div>

      <div className="border-t border-border">
        <div className="container-x py-6 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-muted-foreground">
          <div>© {new Date().getFullYear()} Wolfdog Village Learning Institute. All rights reserved.</div>
          <div className="flex items-center gap-6">
            <Link to="/about" className="hover:text-foreground">Privacy Policy</Link>
            <Link to="/about" className="hover:text-foreground">Terms of Use</Link>
            <Link to="/about" className="hover:text-foreground">Donate</Link>
            <Link to="/about" className="hover:text-foreground">Partner With Us</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}

function FooterCol({ title, links }) {
  return (
    <div>
      <div className="eyebrow mb-4">{title}</div>
      <ul className="space-y-3 text-sm">
        {links.map(([label, to]) => (
          <li key={label}>
            <Link to={to} className="text-muted-foreground hover:text-foreground transition-colors">{label}</Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
